/* @sravni/react-design-system version 0.2.71 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import NumberFormat from 'react-number-format';
import { useFormControlContext, useStyles, error, isNumber, isUndefined } from '@sravni/react-utils';
import { getCurrentStep } from '../../../helpers/getCurrentStep.js';
import '../../TextInput/index.js';
import modules_95586cb6 from '../styles/NumberInput.module.less.js';
import { TextInput } from '../../TextInput/src/TextInput.js';

var rangeValue = function (value, max, min) {
    if (max === undefined && min === undefined) {
        return Number(value);
    }
    if (max === undefined && min !== undefined) {
        return Math.max(value, min);
    }
    if (max !== undefined && min === undefined) {
        return Math.min(value, max);
    }
    return Math.min(max, Math.max(value, min));
};
var roundToNearestStep = function (value, step) {
    var currentStep = getCurrentStep(step, value);
    if (!currentStep) {
        return value;
    }
    if (currentStep < 1) {
        var decimalPart = value - Math.floor(value);
        var integerStep = 1 / currentStep;
        var roundedDecimalPart = Math.round(decimalPart * integerStep) / integerStep;
        return Math.floor(value) + roundedDecimalPart;
    }
    return Math.round(value / currentStep) * currentStep;
};
/**
 * @description Enter a number within certain range with the mouse or keyboard
 *
 * @component
 * @example
 * ```jsx
 * <NumberInput
 *    label="Название поля"
 *    min={30000}
 *    max={1000000}
 *    postfix="₽/мес"
 *    thousandSeparator=" "
 *    decimalScale={2}
 *  />
 * ```
 */
var NumberInput = React.forwardRef(function (props, ref) {
    var value = props.value, min = props.min, max = props.max, step = props.step, decimalScale = props.decimalScale, fixedDecimalScale = props.fixedDecimalScale, decimalSeparator = props.decimalSeparator, thousandSeparator = props.thousandSeparator, invalid = props.invalid, disabled = props.disabled, onChange = props.onChange, onBlur = props.onBlur, onKeyPress = props.onKeyPress, format = props.format, defaultValue = props.defaultValue, className = props.className, restProps = __rest(props, ["value", "min", "max", "step", "decimalScale", "fixedDecimalScale", "decimalSeparator", "thousandSeparator", "invalid", "disabled", "onChange", "onBlur", "onKeyPress", "format", "defaultValue", "className"]);
    var formControlContext = useFormControlContext();
    var cx = useStyles(modules_95586cb6);
    var classNames = cx(className, 'container');
    var condition = decimalSeparator === thousandSeparator;
    // Ensure the decimalSeparator is not equal the thousandSeparator
    if (condition) {
        error({
            condition: condition,
            message: 'Ensure the decimalSeparator is not equal the thousandSeparator',
        })();
    }
    var empty = '';
    var prepareValue = function (value) {
        if (value === undefined || value === empty) {
            return undefined;
        }
        return step ? roundToNearestStep(rangeValue(value, max, min), step) : rangeValue(value, max, min);
    };
    var _a = __read(React.useState(), 2), innerValue = _a[0], setInnerValue = _a[1];
    var isFirstRender = React.useRef(true);
    var handleValueChange = function (values, sourceInfo) {
        if (!sourceInfo.event || values.floatValue === innerValue) {
            return;
        }
        setInnerValue(values.floatValue);
        onChange === null || onChange === void 0 ? void 0 : onChange(values.floatValue);
    };
    var setPreparedValue = function () {
        setInnerValue(function (value) {
            var preparedValue = prepareValue(value);
            if (value !== preparedValue && value !== empty) {
                onChange === null || onChange === void 0 ? void 0 : onChange(preparedValue);
            }
            return preparedValue;
        });
    };
    var handleBlur = function (event) {
        setPreparedValue();
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    };
    var handleKeyPress = function (event) {
        if (event.key === 'Enter') {
            setPreparedValue();
        }
        onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(event);
    };
    React.useEffect(function () {
        if (!isFirstRender.current) {
            isNumber(value) && setInnerValue(value);
            if (isUndefined(value) || value === '') {
                setInnerValue('');
            }
        }
        else {
            setInnerValue(prepareValue(value !== null && value !== void 0 ? value : defaultValue));
            isFirstRender.current = false;
        }
    }, [value]);
    return (React.createElement("div", { "data-qa": "NumberInput", className: classNames },
        React.createElement(NumberFormat, __assign({}, restProps, { getInputRef: ref, value: innerValue, thousandSeparator: thousandSeparator, decimalSeparator: decimalSeparator, decimalScale: decimalScale, fixedDecimalScale: fixedDecimalScale, format: format, allowNegative: false, allowedDecimalSeparators: [',', '.'], inputMode: "decimal", invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, onValueChange: handleValueChange, onKeyPress: handleKeyPress, onBlur: handleBlur, customInput: TextInput }))));
});
NumberInput.defaultProps = {
    decimalSeparator: ',',
    fixedDecimalScale: true,
};
NumberInput.displayName = 'NumberInput';

export { NumberInput };
//# sourceMappingURL=NumberInput.js.map
